import type { BleDeviceInfo } from '@hunting-nature/Secacam/shared/interfaces/ble.interface';
import { createFeatureSelector, createReducer, createSelector, on, type MemoizedSelector } from '@ngrx/store';

import { createScopedSelector } from '../../../shared/utils/factory.utils';
import { RegistrationError, RATE_LIMIT_BACKOFF_MS } from '../../../shared/services/device-api.service';
import { GeneralActions } from '../../../shared/redux/general/general.actions';

import { DeviceRegistrationActions } from './device-registration.actions';

export const DEVICE_REGISTRATION_STORE_KEY = 'deviceRegistration';

/**
 * State machine steps for the device registration flow.
 *
 * Flow: idle → requesting_challenge → awaiting_ble_proof → submitting_proof → success/error
 */
export type RegistrationStep = 'idle' | 'requesting_challenge' | 'awaiting_ble_proof' | 'submitting_proof' | 'success' | 'error';

export interface DeviceRegistrationState {
  /** Current step in the registration state machine */
  step: RegistrationStep;

  /** Challenge session identifier from backend (UUID) */
  challengeId?: string;

  /** Base64-encoded 256-bit random nonce (32 bytes) */
  nonce?: string;

  /** Base64-encoded RSA-4096/SHA-512 signature over the nonce (512 bytes) */
  backendSignature?: string;

  /** Hardware identifier of the registered device (e.g., "Soluna_DS-N4K_4.5-36x40_DEV-2025-00042") */
  hardwareId?: string;

  /** Currently selected Cannes device from BLE scan */
  selectedDevice?: BleDeviceInfo;

  /** Error that occurred during registration (maps to user-facing messages) */
  error?: RegistrationError;

  /** Timestamp until which rate limiting is active. Date.now() + RATE_LIMIT_BACKOFF_MS when set. */
  rateLimitBackoffUntil?: number;
}

export const initialDeviceRegistrationState: DeviceRegistrationState = {
  step: 'idle',
};

/**
 * Reducer for the device registration flow state machine.
 *
 * Handles all transitions between registration steps and error states.
 */
export const deviceRegistrationReducer = createReducer(
  initialDeviceRegistrationState,

  on(
    DeviceRegistrationActions.openDeviceRegistrationFlow,
    (): DeviceRegistrationState => ({
      ...initialDeviceRegistrationState,
      step: 'idle',
    })
  ),

  on(
    DeviceRegistrationActions.closeDeviceRegistrationFlow,
    (): DeviceRegistrationState => ({
      ...initialDeviceRegistrationState,
    })
  ),

  on(
    DeviceRegistrationActions.selectDeviceForRegistration,
    (state: DeviceRegistrationState, action): DeviceRegistrationState => ({
      ...state,
      step: 'requesting_challenge',
      selectedDevice: {
        deviceId: action.deviceId,
        serialNumber: action.serialNumber,
        name: action.modelName,
        isConnected: false,
      },
      error: undefined,
    })
  ),

  on(
    DeviceRegistrationActions.requestChallengeSucceeded,
    (state: DeviceRegistrationState, action): DeviceRegistrationState => ({
      ...state,
      step: 'awaiting_ble_proof',
      challengeId: action.challenge.challengeId,
      nonce: action.challenge.nonce,
      backendSignature: action.challenge.backendSignature,
    })
  ),

  on(
    DeviceRegistrationActions.requestChallengeFailed,
    (state: DeviceRegistrationState, action): DeviceRegistrationState => ({
      ...state,
      step: 'error',
      error: action.error,
    })
  ),

  on(
    DeviceRegistrationActions.collectProofSucceeded,
    (state: DeviceRegistrationState): DeviceRegistrationState => ({
      ...state,
      step: 'submitting_proof',
    })
  ),

  on(
    DeviceRegistrationActions.collectProofFailed,
    (state: DeviceRegistrationState, action): DeviceRegistrationState => ({
      ...state,
      step: 'error',
      error: action.error,
    })
  ),

  on(
    DeviceRegistrationActions.registrationSucceeded,
    (state: DeviceRegistrationState, action): DeviceRegistrationState => ({
      ...state,
      step: 'success',
      hardwareId: action.hardwareId,
    })
  ),

  on(
    DeviceRegistrationActions.registrationFailed,
    (state: DeviceRegistrationState, action): DeviceRegistrationState => ({
      ...state,
      step: 'error',
      error: action.error,
    })
  ),

  on(
    DeviceRegistrationActions.rateLimitBackoffSet,
    (state: DeviceRegistrationState, action): DeviceRegistrationState => ({
      ...state,
      rateLimitBackoffUntil: action.until,
    })
  ),

  on(
    DeviceRegistrationActions.rateLimitExpired,
    (state: DeviceRegistrationState): DeviceRegistrationState => ({
      ...state,
      rateLimitBackoffUntil: undefined,
    })
  ),

  on(
    DeviceRegistrationActions.resetRegistrationState,
    (): DeviceRegistrationState => ({
      ...initialDeviceRegistrationState,
    })
  ),

  on(GeneralActions.logoutSucceeded, (): DeviceRegistrationState => initialDeviceRegistrationState)
);

// Feature selector
const selectDeviceRegistrationState = createFeatureSelector<DeviceRegistrationState>(DEVICE_REGISTRATION_STORE_KEY);

// Scoped selector factory for creating memoized selectors within this feature slice
const createDeviceRegistrationSelector = createScopedSelector(selectDeviceRegistrationState);

/** Helper to explicitly type selectors for use with selectSignal */
const sel = <R>(s: MemoizedSelector<object, R>): MemoizedSelector<object, R> => s;

export const DeviceRegistrationSelectors = {
  step$: sel(createDeviceRegistrationSelector((state: DeviceRegistrationState) => state.step)),

  selectedDevice$: sel(createDeviceRegistrationSelector((state: DeviceRegistrationState) => state.selectedDevice)),

  hardwareId$: sel(createDeviceRegistrationSelector((state: DeviceRegistrationState) => state.hardwareId)),

  error$: sel(createDeviceRegistrationSelector((state: DeviceRegistrationState) => state.error)),

  isFlowActive$: sel(
    createDeviceRegistrationSelector((state: DeviceRegistrationState): boolean => {
      const activeSteps = ['requesting_challenge', 'awaiting_ble_proof', 'submitting_proof'];
      return activeSteps.includes(state.step);
    })
  ),

  canRestart$: sel(
    createDeviceRegistrationSelector((state: DeviceRegistrationState): boolean => {
      if (state.step !== 'error') return false;
      if (state.error === RegistrationError.DEVICE_ALREADY_CLAIMED) return false;
      return true;
    })
  ),

  isSuccess$: sel(createDeviceRegistrationSelector((state: DeviceRegistrationState): boolean => state.step === 'success')),

  hasError$: sel(createDeviceRegistrationSelector((state: DeviceRegistrationState): boolean => state.step === 'error')),
};
